import "./game-loading-overlay.css";

export interface GameLoadingProgress {
  value: number;
  label: string;
  detail: string;
}

export class GameLoadingOverlay {
  private readonly root: HTMLElement;
  private readonly label: HTMLElement;
  private readonly detail: HTMLElement;
  private readonly percent: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly progressbar: HTMLElement;
  private progress = 0;

  constructor(parent: HTMLElement = document.body) {
    this.root = document.createElement("div");
    this.root.className = "game-loading-overlay";
    this.root.hidden = true;
    this.root.setAttribute("aria-hidden", "true");
    this.root.innerHTML = `
      <div class="game-loading-overlay__backdrop" aria-hidden="true"></div>
      <section class="game-loading-panel" aria-labelledby="game-loading-title" aria-live="polite">
        <img class="game-loading-panel__logo" src="/assets/landing/shinobi-idle-logo-v2.webp" alt="Shinobi Idle" width="584" height="284" />
        <span class="game-loading-panel__eyebrow">Entering the world</span>
        <h2 id="game-loading-title" data-role="label">Preparing your journey</h2>
        <p data-role="detail">Connecting to your village and preparing the game world.</p>
        <div class="game-loading-panel__status">
          <span>Game initialization</span>
          <strong data-role="percent">0%</strong>
        </div>
        <div class="game-loading-panel__track" role="progressbar" aria-label="Game loading progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
          <span class="game-loading-panel__fill" data-role="fill"></span>
        </div>
        <small>Progress follows room connection, Phaser asset loading, scene creation, and the first rendered frame.</small>
      </section>
    `;
    parent.append(this.root);

    this.label = this.requireElement("[data-role='label']");
    this.detail = this.requireElement("[data-role='detail']");
    this.percent = this.requireElement("[data-role='percent']");
    this.fill = this.requireElement("[data-role='fill']");
    this.progressbar = this.requireElement("[role='progressbar']");
  }

  begin(label: string, detail: string): void {
    this.progress = 0;
    this.root.hidden = false;
    this.root.setAttribute("aria-hidden", "false");
    document.body.classList.add("is-game-loading");
    this.update({ value: 0.04, label, detail });
  }

  update(progress: GameLoadingProgress): void {
    const next = Math.min(1, Math.max(this.progress, progress.value));
    const percentage = Math.round(next * 100);
    this.progress = next;
    this.label.textContent = progress.label;
    this.detail.textContent = progress.detail;
    this.percent.textContent = `${percentage}%`;
    this.fill.style.width = `${percentage}%`;
    this.progressbar.setAttribute("aria-valuenow", String(percentage));
  }

  hide(): void {
    this.root.hidden = true;
    this.root.setAttribute("aria-hidden", "true");
    document.body.classList.remove("is-game-loading");
  }

  destroy(): void {
    this.hide();
    this.root.remove();
  }

  private requireElement(selector: string): HTMLElement {
    const element = this.root.querySelector<HTMLElement>(selector);
    if (!element)
      throw new Error(`Missing loading overlay element: ${selector}`);
    return element;
  }
}
